import { useLayoutEffect } from 'react';
import { GlobalStyles } from '@mui/material';
import type { GlobalStylesProps } from '@mui/material/GlobalStyles';

/**
 * CSS rules that suppress remote-app global-nav UI when mounted in the PSP shell.
 * Desktop and mobile nav-bar layouts use different `data-test` selectors for the
 * context switcher and profile controls; both layouts must be covered so resize
 * across the mobile breakpoint does not re-enable switching or surface the
 * remote profile avatar.
 */
export const suppressGlobalNavStyles: GlobalStylesProps['styles'] = {
  '#openGovLogo': {
    display: 'none',
  },
  // Desktop profile avatar / menu (wrapped in this container).
  '[data-test="profile_settings_option_container"]': {
    display: 'none',
  },
  // Mobile/tablet profile avatar + utility-tray close control. These sit
  // outside `profile_settings_option_container`, so the desktop rule above
  // does not cover them after a resize into the mobile nav layout.
  '[data-test="profile_button"]': {
    display: 'none !important',
  },
  '[data-test="profile_div"]': {
    display: 'none !important',
  },
  '[data-test="mobile_utility_options_button"]': {
    display: 'none !important',
  },
  '[data-test="mobile_utility_tray_container"]': {
    display: 'none !important',
  },
  // Collapse the padded mobile profile slot wrapper (direct parent of the
  // close IconButton) so it does not leave empty space in the top bar.
  'div:has(> [data-test="mobile_utility_options_button"])': {
    display: 'none !important',
  },
  // Desktop top-bar trigger — replaced by a non-interactive clone via the
  // MutationObserver below.
  '[data-test="context_switcher_trigger"]': {
    display: 'none !important',
  },
  '#non-interactive-context-switcher': {
    paddingLeft: '8px',
    paddingRight: '8px',
    // The nav bar's app-name flex item has no shrink guard, so a remote app with
    // a dense level-one menu can squeeze the label to zero width even though its
    // text is in the DOM.
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  '.context-switcher-header': {
    paddingBottom: 'unset !important',
  },
  // Mobile hamburger menu — same hide pattern as desktop
  // (`display: none` on interactive context-switcher controls). Mobile uses
  // different data-test tags than `context_switcher_trigger`.
  '[data-test="mobile_app_switcher_button"]': {
    display: 'none !important',
  },
  '[data-test="mobile_app_switcher_collapse"]': {
    display: 'none !important',
  },
  '[data-test="mobile_entity_switcher_menu_item"]': {
    display: 'none !important',
  },
  '[data-test="mobile_entity_switcher_icon"]': {
    display: 'none !important',
  },
};

/**
 * Hook that suppresses global navigation UI elements in remote apps.
 * - Converts the desktop context switcher trigger to a non-interactive label
 * - Hides mobile context switcher controls (app + entity)
 * - Hides the OpenGov logo
 * - Hides the profile avatar / menu on desktop and mobile/tablet
 *
 * @returns GlobalStyles component to render
 */
export function useSuppressGlobalNavUi() {
  useLayoutEffect(() => {
    const replaceButtonWithNonInteractiveDiv = () => {
      const appName = document.querySelector(
        '[data-test="context_switcher_trigger"]'
      ) as HTMLElement | null;
      if (!appName) {
        return;
      }

      // Copy the app-name element, whatever tag it happens to be. It was an
      // `h2` until @opengov/components-nav-bar 37.22.1 replaced it with a
      // `span` to fix a heading-order violation, so matching on `h1`-`h6` here
      // silently stopped cloning anything for remote apps on a current nav bar
      // and left them with no product name at all. Match on what the label is
      // *not* instead: the trigger's only other children are the loading
      // spinner and the dropdown caret.
      const appNameNodes = Array.from(appName.children).filter(
        (child) =>
          child.tagName.toLowerCase() !== 'svg' &&
          !child.matches(
            '[data-test="context_switcher_loading"], [data-test="context_switcher_dropdown_arrow"]'
          )
      );
      if (appNameNodes.length === 0) {
        return;
      }

      // Reuse the existing replacement div if present instead of bailing out -
      // the button's app name text changes on navigation (e.g. "Admin
      // Settings" <-> "My Profile"), so a one-time clone goes stale.
      let div = document.querySelector<HTMLDivElement>('[data-psp-replacement-div="true"]');
      const isNewDiv = !div;
      if (!div) {
        div = document.createElement('div');
        div.setAttribute('data-psp-replacement-div', 'true');
        div.id = 'non-interactive-context-switcher';
      }

      // Skip the DOM write when the clone already matches the live button -
      // this observer watches the whole body subtree, so writing
      // unconditionally would retrigger itself on every tick.
      const currentText = appNameNodes.map((node) => node.textContent).join('');
      if (!isNewDiv && div.dataset.pspClonedText === currentText) {
        return;
      }

      div.innerHTML = '';
      appNameNodes.forEach((node) => {
        const clonedLabel = node.cloneNode(true) as HTMLElement;
        clonedLabel.classList.add('context-switcher-header');
        div!.appendChild(clonedLabel);
      });
      div.dataset.pspClonedText = currentText;

      // Insert the div as a sibling after the button (non-destructive)
      if (isNewDiv) {
        appName.parentNode?.insertBefore(div, appName.nextSibling);
      }
    };

    // Try to convert immediately
    replaceButtonWithNonInteractiveDiv();

    // Also set up a MutationObserver to catch dynamically added buttons
    // (including desktop ↔ mobile nav swaps on resize).
    const observer = new MutationObserver(() => {
      replaceButtonWithNonInteractiveDiv();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return <GlobalStyles styles={suppressGlobalNavStyles} />;
}
